import React, { useEffect, useState } from 'react';
import {
  Fab, Typography, withStyles,
} from '@material-ui/core';
import CreateIcon from '@material-ui/icons/Create';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  CardIcon,
} from '~/components/Card';
import PF2MAlertDialog from '~/components/PF2MAlertDialog';
import PF2MButton from '~/components/PF2MButton';
import PF2MTable from '~/components/PF2MTable';
import usePlatformConfiguration from '~/services/platformConfiguration';
import {
  removeAnomalyLog,
  resetOriginalLoadAnomalyList,
  resetOriginalTransportAnomalyList,
  setLoadAnomalyListValues,
  setTransportAnomalyListValues,
  updateAnomalyLogs,
  updateLoadAnomalyList,
  updateTransportAnomalyList,
} from '~/store/dispatch/actions';
import { parseDateToTimestamp } from '~/utils/moment';
import CollapsibleTable from '../CollapsableRow';
import styles from '../styles';

const AnomalyLogsEditModal = ({
  classes, handleClose, data, anomalousTable,
}) => {
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();

  const [dataReport, setDataReport] = useState([]);
  const [columnsReport, setColumnsReport] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const elements = useSelector(state => state.manager.elements);
  const subElements = useSelector(state => state.manager.subElements);
  const materials = useSelector(state => state.nref.materials);
  const transportList = useSelector(state => state.dispatch.transportAnomalyList);
  const loadList = useSelector(state => state.dispatch.loadAnomalyList);
  const codeList = useSelector(state => state.dispatch.anomalyLogsCodes);
  const cloudList = useSelector(state => state.dispatch.anomalyLogsCloud);
  const currentFilter = useSelector(state => state.dispatch.currentAnomalyLogsFilter);
  const anomalyLogs = useSelector(state => state.dispatch.anomalyLogs);
  const isAnomalyLogsDirty = useSelector(state => state.dispatch.isAnomalyLogsDirty);
  const isTransportListDirty = useSelector(state => state.dispatch.isTransportAnomalyListDirty);
  const isLoadListDirty = useSelector(state => state.dispatch.isLoadAnomalyListDirty);
  const ogTransportAnomalyList = useSelector(state => state.dispatch.originalTransportAnomalyList);
  const ogLoadAnomalyList = useSelector(state => state.dispatch.originalLoadAnomalyList);

  const clientReportUrl = usePlatformConfiguration('url_reports_maps');

  const redirecToReport = (row) => {
    const startTimestamp = parseDateToTimestamp(row.start_date, row.start_time);
    const endTimestamp = parseDateToTimestamp(row.end_date, row.end_time);
    const equipId = row.id_equipment;

    const paramsObj = {
      data_inicial: startTimestamp,
      data_final: endTimestamp,
      equip_ids: equipId,
    };

    const searchParams = new URLSearchParams(paramsObj);

    if (clientReportUrl.value === '') {
      setIsModalOpen(true);
      return;
    }

    const reportUrl = new URL(clientReportUrl.value);

    reportUrl.search = searchParams;

    window.open(reportUrl, '_blank');
  };

  const columnsCloudReport = [
    {
      field: 'cnt',
      title: translate('common:Occurrences'),
    },
    {
      field: 'point_name',
      title: translate('common:Polygon'),
    },
    {
      field: 'operator_name',
      title: translate('common:Operator'),
    },
  ];

  const columnsCodeReport = [
    {
      field: 'duration',
      title: translate('common:Duration'),
      type: 'number',
    },
    {
      field: 'point_name',
      title: translate('common:Polygon'),
    },
    {
      field: 'operator_name',
      title: translate('common:Operator'),
    },
  ];

  const columnsTransportReport = [
    {
      field: 'start_date',
      title: translate('common:StartDate'),
      editable: true,
      updateOnChange: true,
      width: '150px',
      datePicker: true,
    },
    {
      field: 'end_date',
      title: translate('common:EndDate'),
      editable: true,
      updateOnChange: true,
      width: '150px',
      datePicker: true,
    },
    {
      field: 'id_mine',
      selectItems: elements,
      editable: true,
      width: '200px',
      title: translate('common:Origin'),
    },
    {
      field: 'id_mine_origin',
      selectItems: subElements,
      editable: true,
      filterValue: 'id_mine',
      filterKey: 'id_element',
      width: '200px',
      title: translate('common:OriginPoint'),
    },
    {
      field: 'id_destination',
      selectItems: elements,
      editable: true,
      width: '200px',
      title: translate('common:Destination'),
    },
    {
      field: 'id_destination_point',
      selectItems: subElements,
      editable: true,
      filterValue: 'id_destination',
      filterKey: 'id_element',
      width: '200px',
      title: translate('common:DestinationPoint'),
    },
    {
      field: 'id_material_truck',
      selectItems: materials,
      editable: true,
      width: '200px',
      title: translate('common:Material'),
    },
  ];

  const columnsLoadReport = [
    {
      field: 'start_date',
      title: translate('common:StartDate'),
      editable: true,
      updateOnChange: true,
      width: '150px',
      datePicker: true,
    },
    {
      field: 'end_date',
      title: translate('common:EndDate'),
      editable: true,
      updateOnChange: true,
      width: '150px',
      datePicker: true,
    },
    {
      field: 'id_mine',
      selectItems: elements,
      editable: true,
      width: '200px',
      title: translate('common:Origin'),
    },
    {
      field: 'id_mine_origin',
      selectItems: subElements,
      filterValue: 'id_mine',
      filterKey: 'id_element',
      editable: true,
      width: '200px',
      title: translate('common:OriginPoint'),
    },
    {
      field: 'id_destination_esc',
      selectItems: elements,
      editable: true,
      width: '200px',
      title: translate('common:Destination'),
    },
    {
      field: 'id_destination_point_esc',
      selectItems: subElements,
      editable: true,
      filterValue: 'id_destination_esc',
      filterKey: 'id_element',
      width: '200px',
      title: translate('common:DestinationPoint'),
    },
    {
      field: 'id_material_esc',
      selectItems: materials,
      editable: true,
      width: '200px',
      title: translate('common:Material'),
    },
  ];

  useEffect(() => {
    let reportList = [];
    switch (anomalousTable) {
      case 1:
        reportList = transportList;
        break;
      case 2:
        reportList = loadList;
        break;
      case 3:
        reportList = codeList;
        break;
      case 4:
        reportList = cloudList;
        break;
      default:
        reportList = [];
    }
    const newDataReport = [1, 2, 3].includes(anomalousTable)
      ? reportList.filter(i => data.some(k => k.uid === i.uid))
      : reportList.filter(i => i.id_equip === data[0].id_equipment);
    // <== temporário, o certo seria: k.anomalous_table_report_id === i.id
    // mas não temos o valor de anomalous_table_report_id
    if (JSON.stringify(dataReport) !== JSON.stringify(newDataReport)) {
      setDataReport(newDataReport);
      switch (anomalousTable) {
        case 1:
          setColumnsReport(columnsTransportReport);
          break;
        case 2:
          setColumnsReport(columnsLoadReport);
          break;
        case 3:
          setColumnsReport(columnsCodeReport);
          break;
        case 4:
          setColumnsReport(columnsCloudReport);
          break;
        default:
          reportList = [];
      }
    }
  },
  [
    dataReport,
    transportList,
    loadList,
    codeList,
    cloudList,
    anomalousTable,
    data,
    columnsTransportReport,
    columnsLoadReport,
    columnsCodeReport,
    columnsCloudReport,
  ]);

  const isSomethingDirty = (isTransportListDirty || isLoadListDirty || isAnomalyLogsDirty);

  const setReportValues = (row, field, value) => {
    switch (anomalousTable) {
      case 1:
        return dispatch(setTransportAnomalyListValues(row, field, value));
      case 2:
        return dispatch(setLoadAnomalyListValues(row, field, value));
      default:
    }
    return null;
  };

  const updateReportList = (columns, filter) => {
    switch (anomalousTable) {
      case 1: {
        const ogData = ogTransportAnomalyList.filter(r => columns.some(({ id }) => r.id === id));
        return dispatch(updateTransportAnomalyList({
          data: columns,
          ogData,
          filters: filter,
          saveLog: true,
        }));
      }
      case 2: {
        const ogData = ogLoadAnomalyList.filter(r => columns.some(({ id }) => r.id === id));
        return dispatch(updateLoadAnomalyList({
          data: columns,
          ogData,
          filters: filter,
        }));
      }
      default:
        return null;
    }
  };

  const restoreOriginalReport = () => {
    switch (anomalousTable) {
      case 1:
        return dispatch(resetOriginalTransportAnomalyList());
      case 2:
        return dispatch(resetOriginalLoadAnomalyList());
      default:
    }
    return null;
  };

  const columns = [
    {
      field: 'date',
      title: translate('common:Date'),
    },
    {
      field: 'time',
      title: translate('common:Time'),
    },
    {
      field: 'anomaly_type_name',
      title: translate('common:AnomalyType'),
    },
  ];

  const renderHeader = () => (
    <div>
      <Typography
        variant="h5"
        align="center"
        gutterBottom
      >
        {`${translate('common:AnomalyLogs')}`}
        <br />
        {`${translate('common:EquipmentGroup')}: ${data[0].equipment_groups_name} | `}
        {`${translate('common:EquipmentType')}: ${data[0].equipment_type_name} | `}
        {`${translate('common:Equipment')}: ${data[0].group_equipament_links_name}`}
      </Typography>
    </div>
  );

  const anomalyColumnRender = ({ lookupData, lookupKey, rowKey }) => (rowData) => {
    const pStyle = { width: 'auto', textAlign: 'left', margin: 'auto' };
    // eslint-disable-next-line react/destructuring-assignment
    const [item] = lookupData.filter(el => el[lookupKey] === rowData[rowKey]);

    return <p style={pStyle}>{item?.name || null}</p>;
  };

  const anomalyColumns = [
    {
      field: 'id_origin_element',
      title: translate('common:ProgrammedOrigin'),
      width: '150px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_origin_element',
          lookupKey: 'id',
          lookupData: elements,
        })
      ),
    },
    {
      field: 'id_origin_subelement',
      title: translate('common:ProgrammedOriginPoint'),
      width: '150px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_origin_subelement',
          lookupKey: 'id',
          lookupData: subElements,
        })
      ),
    },
    {
      field: 'id_material_1',
      title: `${translate('common:ProgrammedMaterial')} 1`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_1',
          lookupKey: 'id',
          lookupData: materials,
        })
      ),
    },
    {
      field: 'id_material_1_destination_subelement_1',
      title: `${translate('common:ProgrammedDestinationPoint1st')} 1`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_1_destination_subelement_1',
          lookupKey: 'id',
          lookupData: subElements,
        })
      ),
    },
    {
      field: 'id_material_1_destination_subelement_2',
      title: `${translate('common:ProgrammedDestinationPoint2nd')} 1`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_1_destination_subelement_2',
          lookupKey: 'id',
          lookupData: subElements,
        })
      ),
    },
    {
      field: 'id_material_2',
      title: `${translate('common:ProgrammedMaterial')} 2`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_2',
          lookupKey: 'id',
          lookupData: materials,
        })
      ),
    },
    {
      field: 'id_material_2_destination_subelement_1',
      title: `${translate('common:ProgrammedDestinationPoint1st')} 2`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_2_destination_subelement_1',
          lookupKey: 'id',
          lookupData: subElements,
        })
      ),
    },
    {
      field: 'id_material_2_destination_subelement_2',
      title: `${translate('common:ProgrammedDestinationPoint2nd')} 2`,
      width: '200px',
      renderCustomComponent: (
        anomalyColumnRender({
          rowKey: 'id_material_2_destination_subelement_2',
          lookupKey: 'id',
          lookupData: subElements,
        })
      ),
    },
  ];


  const renderRow = () => {
    const anomalyType = data[0]?.anomaly_type;

    if ([1, 2].includes(anomalousTable) && [1, 2, 5].includes(anomalyType)) {
      let anomaliesData = [];

      if (anomalousTable === 1) {
        anomaliesData = transportList.filter(al => (
          data.flatMap(({ uid }) => uid).includes(al.uid)
        ));
      } else if (anomalousTable === 2) {
        anomaliesData = loadList.filter(al => (
          data.flatMap(({ uid }) => uid).includes(al.uid)
        ));
      }

      return (
        <CollapsibleTable
          classes={classes}
          dataReport={dataReport}
          columnsReport={columnsReport}
          collapsibleData={anomaliesData}
          collapsibleColumns={anomalyColumns}
          updateItem={(r, field, value) => {
            setReportValues(r, field, value);
          }}
          customItem={anomalousTable === 1
            ? r => redirecToReport(r)
            : null
          }
        />
      );
    }

    return (
      <>
        <PF2MTable
          customStyles={{ height: '250px' }}
          data={dataReport}
          columns={columnsReport}
          updateItem={(row, field, value) => {
            setReportValues(row, field, value);
          }}
        />
      </>
    );
  };

  return (
    <div>
      <Card>
        <CardHeader icon>
          <CardIcon color="warning">
            <CreateIcon />
          </CardIcon>
        </CardHeader>
        <CardBody>
          {renderHeader()}
          {([1, 2].includes(anomalousTable)) && (
            <PF2MTable
              customStyles={{ height: '250px' }}
              data={data}
              columns={columns}
              removeItem={(row) => {
                dispatch(removeAnomalyLog(row));
              }}
            />
          )}
          {renderRow()}
          <div style={{
            display: 'flex',
            marginTop: 10,
            alignItems: 'center',
            justifyContent: 'center',
          }}
          >
            {([1, 2].includes(anomalousTable)) && (
              <>
                <PF2MButton
                  color="primary"
                  onClick={() => {
                    restoreOriginalReport();
                    handleClose(false);
                  }}
                  disabled={!isSomethingDirty}
                >
                  <DeleteOutline className={classes.icon} />
                  {translate('common:DiscardChanges')}
                </PF2MButton>
                <Fab
                  variant="extended"
                  size="medium"
                  color="secondary"
                  disabled={!isSomethingDirty}
                  onClick={() => {
                    updateReportList(dataReport, currentFilter);
                    dispatch(updateAnomalyLogs(anomalyLogs, currentFilter));
                    handleClose(false);
                  }}
                  className={classes.saveButton}
                >
                  {translate('common:SaveData')}
                </Fab>
              </>
            )}
            {!([1, 2].includes(anomalousTable)) && (
              <PF2MButton
                color="primary"
                onClick={() => { handleClose(false); }}
              >
                {translate('common:Ok')}
              </PF2MButton>
            )}
          </div>

        </CardBody>
      </Card>
      <PF2MAlertDialog
        hasCancel={false}
        confirmText={translate('common:Understood')}
        description={translate('validation:ReportUrlNotRegistered')}
        open={isModalOpen}
        onConfirm={() => setIsModalOpen(false)}
      />
    </div>
  );
};

AnomalyLogsEditModal.propTypes = {
  classes: PropTypes.object.isRequired,
  handleClose: PropTypes.func,
  data: PropTypes.array,
  anomalousTable: PropTypes.number,
};

AnomalyLogsEditModal.defaultProps = {
  handleClose: null,
  data: [],
  anomalousTable: 1,
};

export default withStyles(styles)(AnomalyLogsEditModal);
